// A LIVE preview of the destination rendered into the incoming pane while a finger drags. The router renders
// exactly ONE <Outlet/>, and a gesture does not commit the URL until it completes, so the only alternative is a
// frozen snapshot — which can never finish loading, however long you hold the swipe.
// So a SECOND, client-only router is pinned to the destination through a memory history. It shares the parent's
// QueryClient, so what preloadRoute warms on pointer-down lands in THIS tree and a half-completed gesture
// visibly fills in.
// `previewFrom` tells the root route (and a nested surface's host layout) to render a bare <Outlet/>, or the
// preview would repeat the whole document shell inside the pane.

import { useEffect, useState } from 'react'
import { RouterProvider, createMemoryHistory, createRouter, useRouter } from '@tanstack/react-router'
import { routeTree } from '@/routeTree.gen'
import type { RoutePath } from './surface.config'

// A layout hosting a NESTED surface must render only its <Outlet/> when this equals its own path, or the preview
// repeats that layout's chrome inside the tab-sized pane it is supposed to fill.
export function useTransitionPreviewFrom(): RoutePath | null {
  const context = useRouter().options.context as { previewFrom?: RoutePath }
  return context.previewFrom ?? null
}

// Where this component stands relative to the surface being previewed: 'passthrough' is chrome AROUND the pane
// (render only the subtree leading to the surface), 'above' is ordinary content inside someone else's pane, and
// 'aside' is an unrelated branch.
// BY SEGMENT, NEVER BY PREFIX STRING: startsWith('/teams') says yes to /teams-archive/1, and — the case that bit —
// the ROOT surface previews with scope '/', a string prefix of everything, so every gate needed its own guard.
// Here '/' is simply ABOVE every scope, which is what it is.
export type PreviewMode = 'live' | 'passthrough' | 'above' | 'aside'

export function usePreviewMode(scope: RoutePath): PreviewMode {
  const previewFrom = useTransitionPreviewFrom()
  if (previewFrom === null) return 'live'
  if (previewFrom === scope || isUnder(previewFrom, scope)) return 'passthrough'
  return isUnder(scope, previewFrom) ? 'above' : 'aside'
}

/** Is `path` strictly below `base`, counted in SEGMENTS — `/teams/42` is under `/teams`, `/teams-archive` isn't. */
function isUnder(path: RoutePath, base: RoutePath): boolean {
  if (base === '/') return path !== '/'
  return path.startsWith(base.endsWith('/') ? base : `${base}/`)
}

// A preview's history is a memory one, so a navigation started inside this tree would move only the shadow router
// and the pane would show a page the app is not on.
// On interactiveIncoming surfaces this is load-bearing rather than held in reserve: a link tapped on the arriving
// page mid-settle moves the REAL router, and the transition resolves against it like any other navigation.
// The INITIAL load is not a navigation and must not forward — `fromLocation` is what tells them apart.
export function RoutePreview(props: { to: RoutePath; previewFrom: RoutePath }) {
  const parent = useRouter()
  // Created once per mount; the host re-keys on `to`, so a different destination is a fresh router.
  const [previewRouter] = useState(() =>
    createRouter({
      routeTree,
      history: createMemoryHistory({ initialEntries: [props.to] }),
      context: { ...parent.options.context, previewFrom: props.previewFrom },
      // This tree IS the preload of the destination, so it must never fan out preloads of its own.
      defaultPreload: false,
      defaultPreloadStaleTime: 0,
      // A FRESH router doing an INITIAL load gates its pending UI on the router's own timers, not Suspense — so with
      // defaultPendingMs at 1000 the pane sat blank for the whole slide on a slow network, and the skeleton appeared
      // only at the reveal. Zeroed so the route's own skeleton paints immediately, matching a real link navigation.
      // A warm cache is unaffected: nothing pends, so there is no pending window to time.
      defaultPendingMs: 0,
      defaultPendingMinMs: 0,
      // No defaultPendingComponent, as in the real router: a route without a skeleton presents nothing while it loads.
      // A preview that throws is not worth surfacing: if the failure is real, the navigation on commit runs its own
      // loaders and shows the real error.
      defaultErrorComponent: () => null,
    }),
  )
  // Subscribed once: `previewRouter` is created once per mount and `parent` never changes identity.
  useEffect(
    () =>
      previewRouter.subscribe('onBeforeNavigate', (event) => {
        const to = event.toLocation.href
        // The initial load carries no `fromLocation`; a real navigation does.
        if (!event.fromLocation || to === parent.state.location.href) return
        void parent.navigate({ to })
      }),
    [previewRouter, parent],
  )
  return <RouterProvider router={previewRouter} />
}
